export interface LocalStorageInterface {
  // Auth tokens
  getRefreshToken: () => string | null
  hasRefreshToken: () => boolean
  getIDToken: () => string | null
  storeTokens: (refreshToken: string | null, idToken: string | null) => void
  clearAuthTokens: () => void
  clearAllLocalStorage: () => void

  // Flags
  setLocalStorageFlag: (flag: string) => void
  setNewUserLocalStorageFlags: () => void

  needsToSeeVerifyFreemiumPopup: () => boolean
  clearNeedsToSeeVerifyFreemiumPopup: () => void
}

// constant object for storage items
export const localStorageItems = {
  refreshToken: "REFRESH",
  idToken: "TOKEN",
  analyticsFamilyId: "familyId",
  analyticsUserId: "userId",

  onboardingCustomPlanExperiment: "onboardingCustomPlanExperiment",

  // Onboarding
  needsToSeePaywall: "needsToSeePaywall",
  needsToSeePrePaywall: "needsToSeePrePaywall",
  needsToSeeVerifyFreemiumPopup: "VERIFY_FREEMIUM_POPUP",
  needsToFireRegistrationEvent: "FIRE_REGISTRATION_EVENT",
  wasRoutedFromExternalCoachMarketing: "wasRoutedFromExternalCoachMarketing",
  needsToRefreshQuests: "needsToRefreshQuests",

  isFromCoachOnboarding: "isFromCoachOnboarding",
  recommendedCoaches: "recommendedCoaches",
  // Branching-CTA entry-point tag from the jooncoach.com landing page
  // (?focus=meltdowns|routines|all). Persisted so it survives the auth
  // round-trip and is available for analytics joins post-signup.
  coachOnboardingFocus: "coachOnboardingFocus",

  // Referral
  hasSeenReferralFTUXModal: "hasSeenReferralFTUXModal",
  hasDismissedReferralGiftCardSection: "hasDismissedReferralGiftCardSection",
  joonGiftCardReferralCode: "joonGiftCardReferralCode",

  // First-time user experience when linked to a therapist
  needsToSeeTherapistTour: "needsToSeeTherapistTour",
  needsToSeeWelcomeRewards: "needsToSeeWelcomeRewards",
  needsToSeeWelcomeQuests: "needsToSeeWelcomeQuests",

  showABAInviteSection: "showABAInviteSection",
  parentQuestionnaireAnswers: "parentQuestionnaireAnswers",
  coachQuestionnaireAnswers: "coachQuestionnaireAnswers",
  numPreferredSessions: "numPreferredSessions",
  coachOnboardingComplete: "coachOnboardingComplete",
  stripeFromPaywall: "stripeFromPaywall",
  planChoiceFromPaywall: "planChoiceFromPaywall",
  coachChoiceStripeCheckout: "coachChoiceStripeCheckout",
  redirectLink: "redirectLink",
  isCoparent: "isCoparent",
  tuneId: "tuneId",
  therapistCode: "therapistCode",

  needsToRefreshBookings: "needsToRefreshBookings",

  lastSeenPaywall: "lastSeenPaywall",

  hasSeenScreenTimeControlsModal: "hasSeenScreenTimeControlsModal",
  hasSeenSTRestrictionFTUXModal: "hasSeenSTRestrictionFTUXModal",
  needsToSeeWelcomeModal: "needsToSeeWelcomeModal",

  screentimeReminderDismissed: "screentimeReminderDismissed", // has respective reward id concatenated

  // Add Edit Quest Modal switches
  prefersToReviewQuest: "prefersToReviewQuest",
  prefersToReviewWithPicture: "prefersToReviewWithPicture",
  prefersToReviewWithNotes: "prefersToReviewWithNotes",

  // On first complete/verify quest
  hasMarkedFirstQuestComplete: "hasMarkedFirstQuestComplete",

  // Coach in app upsells
  rejectCoachUpsellModalSeenDate: "rejectCoachUpsellModalSeenDate",
  deleteCoachUpsellModalSeenDate: "deleteCoachUpsellModalSeenDate",
  coachUpsellBannerSeen: "coachUpsellBannerSeen",

  // Coach tab overlays
  lastSeenSessionBookingOverlay: "lastSeenSessionBookingOverlay",
  lastSeenNewRoutinesOverlay: "lastSeenNewRoutinesOverlay",

  // AI Quest Builder
  hasSeenAIQuestBuilderWelcome: "hasSeenAIQuestBuilderWelcome",
  chatSessionId: "chatSessionId",

  // Locale
  locale: "locale",

  // Country (for region-based pricing)
  country: "country",
} as const

export const sessionManager: LocalStorageInterface = {
  // TOKENS
  getRefreshToken: () => localStorage.getItem(localStorageItems.refreshToken),
  hasRefreshToken: () => !!localStorage.getItem(localStorageItems.refreshToken),
  getIDToken: () => localStorage.getItem(localStorageItems.idToken),
  storeTokens: (refreshToken: string | null, idToken: string | null) => {
    refreshToken &&
      localStorage.setItem(localStorageItems.refreshToken, refreshToken)
    idToken && localStorage.setItem(localStorageItems.idToken, idToken)
  },
  clearAuthTokens: () => {
    localStorage.removeItem(localStorageItems.refreshToken)
    localStorage.removeItem(localStorageItems.idToken)
  },
  clearAllLocalStorage: () => {
    const storageTokensToKeep = [
      localStorageItems.hasSeenScreenTimeControlsModal,
      localStorageItems.hasSeenAIQuestBuilderWelcome,
      localStorageItems.hasMarkedFirstQuestComplete,
    ] as string[]
    Object.values(localStorageItems).forEach((key) => {
      if (storageTokensToKeep.includes(key)) return
      localStorage.removeItem(key)
    })
  },

  // FLAGS
  setLocalStorageFlag: (flag: string) => localStorage.setItem(flag, "true"),
  setNewUserLocalStorageFlags: function (this: LocalStorageInterface) {
    // Hide download app banner until user sees the modal
    this.setLocalStorageFlag(localStorageItems.needsToSeePaywall)
    this.setLocalStorageFlag(localStorageItems.needsToFireRegistrationEvent)
    this.setLocalStorageFlag(localStorageItems.needsToSeeVerifyFreemiumPopup)
    this.setLocalStorageFlag(localStorageItems.needsToRefreshQuests)
    // Hide referral gift card FTUX until user finishes welcome flow
    this.setLocalStorageFlag(localStorageItems.hasSeenReferralFTUXModal)
  },

  needsToSeeVerifyFreemiumPopup: () =>
    !!localStorage.getItem(localStorageItems.needsToSeeVerifyFreemiumPopup),
  clearNeedsToSeeVerifyFreemiumPopup: () =>
    localStorage.removeItem(localStorageItems.needsToSeeVerifyFreemiumPopup),
}
